/**
 * Ratings Cards Carousel Web Component
 * Auto-scrolling carousel for mobile view
 */

import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

export class RatingsCarousel extends Component {
  constructor() {
    super();
    
    this.originalCards = [];
    this.clonedCards = [];
    this.animationId = null;
    this.scrollPosition = 0;
    this.isHovered = false;
    this.mediaQuery = window.matchMedia('(max-width: 1099px)');
    this.isInitialized = false;
    this.observer = null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Lazy init only when visible
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          requestIdleCallback(() => this.lazyInit());
          this.observer?.disconnect();
        }
      });
    }, { rootMargin: '50px' });

    this.observer.observe(this);
  }

  lazyInit() {
    // Get carousel from refs
    if (!this.refs.carousel) return;

    // Only initialize if mobile
    if (!this.mediaQuery.matches) {
      this.setupMediaListener();
      return;
    }
    
    this.init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.observer?.disconnect();
    this.destroy();
  }

  init() {
    this.setupEventListeners();
    this.initCarousel();
  }

  initCarousel() {
    const carousel = this.refs.carousel;
    if (!carousel || !this.mediaQuery.matches) return;

    // Initialize cards only once
    if (this.originalCards.length === 0) {
      this.originalCards = Array.from(carousel.children).filter(card =>
        !card.hasAttribute('data-cloned')
      );

      this.cloneCardsToFill();
    }

    this.scrollPosition = 0;
    carousel.style.transform = 'translate3d(0, 0, 0)';

    if (!this.animationId) {
      this.startAnimation();
    }
    
    this.isInitialized = true;
  }

  startAnimation() {
    const carousel = this.refs.carousel;
    if (!carousel) return;

    const scrollSpeed = 0.75;
    const originalWidth = this.getOriginalWidth();
    if (originalWidth === 0) return;

    const autoScroll = () => {
      if (!this.isHovered) {
        this.scrollPosition += scrollSpeed;

        if (this.scrollPosition >= originalWidth) {
          this.scrollPosition -= originalWidth;
        }

        carousel.style.transform = `translate3d(-${this.scrollPosition}px, 0, 0)`;
      }

      this.animationId = requestAnimationFrame(autoScroll);
    };

    this.animationId = requestAnimationFrame(autoScroll);
  }

  setupEventListeners() {
    const carousel = this.refs.carousel;
    if (!carousel) return;

    // Hover state tracking
    carousel.addEventListener('mouseenter', () => {
      this.isHovered = true;
    }, { passive: true });

    carousel.addEventListener('mouseleave', () => {
      this.isHovered = false;
    }, { passive: true });

    this.setupMediaListener();
  }

  setupMediaListener() {
    // Media query change
    const handleMediaChange = (e) => {
      if (e.matches && !this.isInitialized) {
        this.initCarousel();
      } else if (!e.matches && this.isInitialized) {
        this.stopCarousel();
      }
    };

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', handleMediaChange, { passive: true });
    } else {
      this.mediaQuery.addListener(handleMediaChange);
    }
  }

  stopCarousel() {
    const carousel = this.refs.carousel;
    
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.clonedCards.forEach(clone => clone.remove());
    this.clonedCards = [];
    if (carousel) {
      carousel.style.transform = '';
    }
    this.scrollPosition = 0;
    this.isInitialized = false;
  }

  getOriginalWidth() {
    if (!this.originalCards.length) return 0;
    const first = this.originalCards[0];
    const last = this.originalCards[this.originalCards.length - 1];
    const firstLeft = first.offsetLeft;
    const lastRight = last.offsetLeft + last.getBoundingClientRect().width;
    return Math.max(0, lastRight - firstLeft);
  }

  cloneCardsToFill() {
    const carousel = this.refs.carousel;
    if (!carousel) return;

    // Clear any previous clones if re-initializing
    this.clonedCards.forEach(clone => clone.remove());
    this.clonedCards = [];

    const originalWidth = this.getOriginalWidth();
    if (originalWidth === 0) return;

    const containerWidth = carousel.parentElement?.getBoundingClientRect().width || 0;
    const minFillWidth = Math.max(containerWidth * 2, originalWidth * 2);

    let filledWidth = originalWidth;
    while (filledWidth < minFillWidth) {
      this.originalCards.forEach(card => {
        const clone = card.cloneNode(true);
        clone.setAttribute('data-cloned', 'true');
        carousel.appendChild(clone);
        this.clonedCards.push(clone);
      });
      filledWidth += originalWidth;
    }
  }

  destroy() {
    this.stopCarousel();
    this.originalCards = [];
  }
}

// Define custom element
if (!customElements.get('ratings-carousel')) {
  customElements.define('ratings-carousel', RatingsCarousel);
}
